import React from 'react';
import { Box, Typography, Paper, Container, Link, List, ListItem } from '@mui/material';

const TermsOfServicePage: React.FC = () => {
  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          📋 Пользовательское соглашение
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Дата вступления в силу: 05.04.2026 года
        </Typography>

        <Paper sx={{ p: 4, mt: 3 }}>
          {/* Вводная часть */}
          <Typography paragraph>
            Настоящее Пользовательское соглашение (далее — «Соглашение») является публичной офертой
            и регулирует отношения между владельцем (далее — «Оператор») сервиса «nashrelax.ru»
            (далее — «Сервис») и любым физическим лицом, использующим данный сервис (далее — «Пользователь»).
          </Typography>
          <Typography paragraph>
            Настоящее Соглашение определяет порядок использования Сервиса, права и обязанности сторон.
          </Typography>
          <Typography paragraph>
            Используя Сервис, расположенный по адресу <strong>https://nashrelax.ru</strong>, вы подтверждаете,
            что ознакомились с условиями настоящего Соглашения и{' '}
            <Link href="/privacy-policy" underline="hover">Политики конфиденциальности</Link>,
            а также обязуетесь их соблюдать.
          </Typography>
          <Typography paragraph>
            Персональные данные — любая информация, относящаяся прямо или косвенно к определённому
            пользователю (в соответствии с Федеральным законом № 152-ФЗ).
          </Typography>

          {/* 1. Термины и определения */}
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
            1. Термины и определения
          </Typography>
          <List dense>
            <ListItem><strong>«Рекомендации»</strong> — информация, направляемая Сервисом Пользователю любым способом, не противоречащим законодательству Российской Федерации, в том числе рекламная информация.</ListItem>
            <ListItem><strong>«Соглашение»</strong> — настоящее пользовательское соглашение.</ListItem>
            <ListItem><strong>«Стороны»</strong> — пользователь и Сервис nashrelax.ru</ListItem>
            <ListItem><strong>«Подписка»</strong> – пакет опций, при котором Пользователь в течение определённого в Соглашении периода за вознаграждение (стоимость подписки) получает доступ к набору дополнительных опций.</ListItem>
            <ListItem><strong>«Дополнительные Опции»</strong> – опции Приложения, приобретаемые Пользователем отдельно вне Подписки.</ListItem>
            <ListItem><strong>«Организатор мероприятия»</strong> - физическое или юридическое лицо, являющееся организатором мероприятия, информация о котором размещена в приложении.</ListItem>
          </List>

          {/* 2. Предмет соглашения */}
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
            2. Предмет соглашения
          </Typography>
          <Typography paragraph>
            2.1. Сервис nashrelax.ru является Интернет-ресурсом, предназначенным для публикации и
            получения информации о различных мероприятиях и местах проведения досуга, а также для
            знакомств Пользователей.
          </Typography>
          <Typography paragraph>
            2.2. Оператор предоставляет пользователю доступ к информации, сервисам и функциям Сайта,
            а пользователь обязуется использовать их в соответствии с настоящим Соглашением и
            действующим законодательством РФ.
          </Typography>
          <Typography paragraph>
            2.3. Пользователь гарантирует достоверность и актуальность предоставленных данных.
          </Typography>
          <Typography paragraph>
            2.4. Пользователь несёт ответственность за все действия, совершённые с использованием
            его учётной записи. Запрещается передавать данные для входа третьим лицам.
          </Typography>
          <Typography paragraph>
            2.5. Если иное прямо не предусмотрено соглашением между Сторонами, Сервис не является
            организатором мероприятий, продавцом каких-либо товаров или услуг. Все обязательства по
            поводу проведения мероприятий или оказания соответствующих услуг возникают между
            Пользователем и организатором мероприятия.
          </Typography>
          <Typography paragraph>
            2.6. Сервис не несёт ответственности за безопасность мероприятий и достоверность
            информации, указанной организатором.
          </Typography>

          {/* 3. Регистрация и использование */}
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
            3. Регистрация и использование Сервиса
          </Typography>
          <Typography paragraph>
            3.1. Для использования полной функциональности Сервиса Пользователь обязан пройти
            процедуру регистрации или авторизоваться или воспользоваться сервисами Google/Yandex
            для OAuth-авторизации.
          </Typography>
          <Typography paragraph>
            3.2. При использовании Сайта Администрация может собирать следующие данные:
          </Typography>
          <ul>
            <li>Фамилия, имя, отчество</li>
            <li>Адрес электронной почты</li>
            <li>Пол</li>
            <li>Номер телефона</li>
            <li>Логин</li>
            <li>Сведения, собираемые посредством метрических программ</li>
            <li>Сведения, предоставленные пользователем добровольно (например, при заполнении карточки мероприятия или при заполнении формы обратной связи)</li>
          </ul>

          {/* 4. Права и обязанности сторон */}
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
            4. Права и обязанности сторон
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
            4.1. Пользователь обязуется:
          </Typography>
          <ul>
            <li>Соблюдать условия настоящего Соглашения и действующего законодательства РФ</li>
            <li>Знакомиться с актуальной версией настоящего Соглашения при каждом посещении Сервиса</li>
            <li>Самостоятельно обеспечивать конфиденциальность своих учётных данных (логин, пароль)</li>
            <li>Предоставлять достоверные данные при заполнении форм</li>
            <li>Не предпринимать действий, способных нарушить работу Сайта</li>
            <li>Не использовать Сервис для распространения информации, нарушающей права третьих лиц или законодательство, в том числе содержащие:
              <ul>
                <li>спам и/или рекламу спама и/или ссылку на спам</li>
                <li>недостоверную информацию</li>
                <li>ненормативную лексику</li>
                <li>авторский материал и торговые марки третьих лиц, используемые без разрешения правообладателя</li>
                <li>материалы эротического, порнографического или оскорбительного характера</li>
                <li>специальные категории персональных данных в понимании Статьи 10 Федерального закона РФ №152-ФЗ «О защите персональных данных»</li>
                <li>материалы, содержащие информацию о финансовых пирамидах, запрещённых медицинских препаратах, наркотических веществах, любых курительных смесях и жидкостях, табаке, приспособлениях для курения, данные об алкоголе и иных веществах, вызывающих зависимость или несущих вред здоровью, в том числе о «спортивном питании»</li>
                <li>политическую агитацию</li>
              </ul>
            </li>
          </ul>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
            4.2. Оператор обязуется:
          </Typography>
          <ul>
            <li>Предоставлять Пользователю доступ к Сервису в соответствии с условиями настоящего Соглашения</li>
            <li>Обеспечивать сохранность персональных данных Пользователя в соответствии с Политикой конфиденциальности и Федеральным законом № 152-ФЗ «О персональных данных»</li>
            <li>Принимать разумные меры для защиты Сервиса от несанкционированного доступа и сбоев в работе</li>
            <li>Не передавать данные третьим лицам без согласия пользователя, за исключением случаев, предусмотренных законом</li>
          </ul>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
            4.3. Пользователь имеет право:
          </Typography>
          <ul>
            <li>Использовать Сайт для получения информации, оформления заявок и других законных целей</li>
            <li>Направлять Администрации обращения и запросы</li>
            <li>Отозвать своё согласие на обработку персональных данных</li>
          </ul>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
            4.4. Оператор имеет право:
          </Typography>
          <ul>
            <li>Производить модификацию любого программного обеспечения Приложения, приостанавливать работу Приложения при обнаружении существенных неисправностей, ошибок и сбоев, а также в целях проведения профилактических работ и предотвращения случаев несанкционированного доступа к Приложению</li>
            <li>Вносить изменения в настоящее Соглашение без предварительного уведомления</li>
            <li>Использовать анонимные данные пользователей для статистики, аналитики и улучшения сервиса</li>
            <li>При наличии согласия Пользователя направлять Пользователю рекламные, информационные и другие сообщения, как по адресу электронной почты, так и по номеру мобильного телефона, сведения о которых содержатся в Приложении</li>
          </ul>

          {/* 5. Права на контент */}
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
            5. Права на контент
          </Typography>
          <Typography paragraph>
            5.1. Все права на программное обеспечение, дизайн и структуру Сервиса принадлежат Оператору.
          </Typography>
          <Typography paragraph>
            5.2. Информация, которую Пользователь размещает в Сервисе (текстовые описания мероприятий,
            условия посещения), может быть доступна другим Пользователям Сервиса. Размещая такую
            информацию, Пользователь подтверждает своё право на её распространение в рамках Сервиса.
          </Typography>

          {/* 6. Конфиденциальность */}
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
            6. Конфиденциальность и персональные данные
          </Typography>
          <Typography paragraph>
            6.1. Обработка персональных данных Пользователя осуществляется в соответствии с{' '}
            <Link href="/privacy-policy" underline="hover">Политикой конфиденциальности</Link>,
            являющейся неотъемлемой частью настоящего Соглашения.
          </Typography>
          <Typography paragraph>
            6.2. Пользователь даёт согласие на сбор, хранение, обработку и использование своих
            персональных данных для целей оказания услуг Сервиса (идентификация, создание мероприятий,
            отправка уведомлений).
          </Typography>
          <Typography paragraph>
            6.3. Пользователь имеет право отозвать согласие на обработку персональных данных в любой
            момент, направив запрос на адрес электронной почты:{' '}
            <strong>asc.asp@yandex.ru</strong>.
          </Typography>

          {/* 7. Ограничение ответственности */}
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
            7. Ограничение ответственности
          </Typography>
          <Typography paragraph>
            7.1. Оператор не несёт ответственности за:
          </Typography>
          <ul>
            <li>Временную недоступность Сервиса по техническим причинам, связанным с проведением профилактических работ или сбоями в работе оборудования</li>
            <li>Ущерб, возникший вследствие предоставления Пользователем недостоверных данных или несанкционированного доступа третьих лиц к учётной записи Пользователя по его вине</li>
            <li>Действия или бездействие третьих лиц (например, провайдеров услуг связи)</li>
            <li>Сервис ни при каких обстоятельствах не несёт ответственности за исполнение или неисполнение организатором мероприятия обязанности по возврату денежных средств Пользователю в случае отказа Пользователя от участия в мероприятии, либо отмены или переноса мероприятия организатором. Все претензии, связанные с указанными действиями и событиями, Пользователь должен направлять непосредственно организатору</li>
          </ul>

          {/* 8. Изменение условий Соглашения */}
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
            8. Изменение условий Соглашения
          </Typography>
          <Typography paragraph>
            8.1. Оператор оставляет за собой право вносить изменения в настоящее Соглашение в
            одностороннем порядке.
          </Typography>
          <Typography paragraph>
            8.2. Новая редакция Соглашения вступает в силу с момента её размещения в Сервисе.
            Продолжение использования Сервиса после внесения изменений означает согласие Пользователя
            с новой редакцией Соглашения.
          </Typography>

          {/* 9. Заключительные положения */}
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
            9. Заключительные положения
          </Typography>
          <Typography paragraph>
            9.1. Настоящее Соглашение регулируется законодательством Российской Федерации.
          </Typography>
          <Typography paragraph>
            9.2. Все споры и разногласия решаются путём переговоров. В случае невозможности достижения
            согласия — в судебном порядке по месту нахождения Оператора.
          </Typography>
          <Typography paragraph>
            9.3. По вопросам обработки персональных данных или работы Сервиса Пользователь может
            обращаться по адресу: <strong>asc.asp@yandex.ru</strong>.
          </Typography>
          <Typography paragraph>
            Оператором персональных данных является <strong>Поляков Александр Сергеевич</strong>.
          </Typography>

          <Box sx={{ mt: 4, p: 3, bgcolor: 'warning.light', borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>Важно:</strong> Используя Сервис, вы подтверждаете, что ознакомились
              с Пользовательским соглашением и соглашаетесь с его условиями.
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Link href="/events" underline="hover">
            ← Вернуться к мероприятиям
          </Link>
          <Link href="/privacy-policy" underline="hover" target="_blank" rel="noopener noreferrer">
            Политика конфиденциальности →
          </Link>
          <Link href="/community-rules" underline="hover" target="_blank" rel="noopener noreferrer">
            Правила сообщества →
          </Link>
        </Box>
      </Box>
    </Container>
  );
};

export default TermsOfServicePage;
